/* istanbul ignore file */
import React from 'react';
import { batch, useSelector } from 'react-redux';
import { useCurator } from '@noths/curator-web';
import {
  type DeliveryZoneCode,
  parseLocaleSetCookieHeaders,
} from '@noths/polaris-client-localisation';
import { NotFound } from '@noths/polaris-client-next-components';
import { DepartmentBanner } from '@noths/polaris-client-ribbons-design-system';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { generateRandomString } from '@noths/polaris-client-utils';
import { type CurrencyCode, StatusCode } from '@noths/polaris-dev-ts-types';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { parse } from 'url';

import { adaptPageSettingsToStore } from 'src/adapters/contentToStore/adaptPageSettingsToStore';
import { adaptTaxonomyPlpEntryToStore } from 'src/adapters/contentToStore/adaptTaxonomyPlpEntryToStore';
import { buildListingsRequestFromUrl } from 'src/builders/buildListingsRequestFromUrl';
import type { TaxonomyListingOwnProps } from 'src/components/layouts/TaxonomyListing/TaxonomyListing';
import { TaxonomyListingContainer } from 'src/components/layouts/TaxonomyListing/TaxonomyListingContainer';
import { useTaxonomyBreadcrumbs } from 'src/components/molecules/Breadcrumbs/useTaxonomyBreadcrumbs';
import { useTaxonomySeoTags } from 'src/components/organisms/TaxonomyHeaderBlock/useTaxonomySeoTags';
import {
  SHOP_SURFACE_ALL,
  SHOP_TAXONOMY_ALIASES,
  SHOP_VALID_SURFACES,
} from 'src/constants/shopTaxonomy';
import { isProd } from 'src/environment';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectShopPageTitle } from 'src/redux/content/selectors/selectShopPageTitle';
import type { TaxonomyEntry } from 'src/redux/content/types';
import { mergeTaxonomyContentEntryIntoEntries } from 'src/redux/content/utils/mergeTaxonomyContentEntryIntoEntries';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { PRODUCTS_LIST_DATA_FETCHING_ERROR } from 'src/redux/products/thunks/thunkErrors';
import { getInitialListingsControlsState } from 'src/redux/productsQuery/helpers/getInitialListingsControlsState';
import { syncFromURL } from 'src/redux/productsQuery/thunks/syncFromURL';
import { selectPagePropsForClientHydration } from 'src/redux/selectors';
import { getStore } from 'src/redux/store';
import { actions as userActions } from 'src/redux/user/slice';
import { getLDBreadcrumbListSchema } from 'src/seo/linkedData/getLDBreadcrumbListSchema';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { fetchListingsControlsConfig } from 'src/services/contentstack/fetchListingsControlsConfig';
import { fetchPLPSettings } from 'src/services/contentstack/fetchPLPSettings';
import { getContentstackEnvironment } from 'src/services/contentstack/getContentstackEnvironment';
import { fetchTaxonomyPlpNeighborhoodMap } from 'src/services/contentstack/taxonomyNeighborhood/fetchTaxonomyPlpNeighborhoodMap';
import { fetchTaxonomyPlpContent } from 'src/services/contentstack/taxonomyPlpContent/fetchTaxonomyPlpContent';
import { getBrowseServingConfig } from 'src/services/listings-api/servingConfigs';
import { fetchDeliveryEvent } from 'src/services/mononoth/fetchDeliveryEvent';
import { getInternalLinks } from 'src/services/similar-ai/getInternalLinks';
import { getServerSidePageEventObject } from 'src/tracking/events/pageEvent';
import { PageType } from 'src/types/navigation';
import type { NextConfig } from 'src/types/nextConfig';
import { selectTaxonomyContentEntry } from 'src/utils/selectTaxonomyContentEntry';
import { logger } from 'src/utils/serverLogger';

const Curator = dynamic(
  () =>
    import('@noths/curator-web').then(async (m) => {
      m.configureCurator({
        env: isProd() ? 'production' : 'qa',
        hideChatToggleForMobileViewport: true,
      });
      return m.Curator;
    }),
  { ssr: false },
);

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query, req, res } = context;
  const slugSegments = Array.isArray(query.slug) ? query.slug : query.slug ? [query.slug] : [];
  const surfaceSegment = slugSegments[0];

  // Only `all` is a recognised surface today; anything else is a 404. Bare
  // `/shop` is redirected to `/shop/all` by the middleware before this handler
  // runs, so `surfaceSegment` is always defined at this point. Legacy
  // /shop/<taxonomy>/... URLs are intentionally not back-redirected here —
  // they are handled by a separate redirect programme.
  if (surfaceSegment === undefined || !SHOP_VALID_SURFACES.includes(surfaceSegment)) {
    return { notFound: true };
  }

  const preview = false;
  const featureFlags = getFeatureFlagsWithMatchers(req);
  const pathname = parse(req.url!).pathname!;
  const env = getContentstackEnvironment(preview);
  const { _ga: googleAnalyticsVisitorId, cognitoId, currency, delivery_zone } = req.cookies;
  const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
    parseLocaleSetCookieHeaders(res.getHeaders());
  const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
  /* One expression feeding both the store and the request, so this page cannot
     seed one zone and ask for another. It can still be undefined: a visitor with
     no locale header and no cookie has no zone, which is a real case rather than
     a gap. What that means is decided once downstream, in `adaptStoreToRequestBody`,
     so it is deliberately not defaulted here. Same narrowing as `currencyCode`
     above. */
  const deliveryZoneCode = (initialDeliveryZone ?? delivery_zone) as DeliveryZoneCode | undefined;
  const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);

  // Site chrome is deliberately absent here and fetched in the final wave
  // instead. Nothing between this point and the props object needs it, so
  // leaving it in this barrier would delay the taxonomy-content hop and the
  // listings request behind a call neither of them depends on.
  const [settings, deliveryEvent, fetchedListingsControlsConfig, neighborhoodMap] =
    await Promise.all([
      fetchPLPSettings({ env }),
      fetchDeliveryEvent(),
      fetchListingsControlsConfig(),
      fetchTaxonomyPlpNeighborhoodMap({ path: pathname }).catch(() => []),
    ]);

  const matchedNeighborhoodEntry = selectTaxonomyContentEntry(pathname, neighborhoodMap);

  const fetchedTaxonomyPlp = matchedNeighborhoodEntry
    ? await fetchTaxonomyPlpContent({ env, urlPath: matchedNeighborhoodEntry.url })
    : null;

  if (!fetchedListingsControlsConfig) {
    logger.error('shop PLP: listings controls config unavailable');
    return { notFound: true };
  }

  const { facets: configFacets, sortOptions } = getInitialListingsControlsState(
    fetchedListingsControlsConfig,
    {
      deliveryEvent: deliveryEvent && {
        date: deliveryEvent.last_deliver_by,
        name: deliveryEvent.name,
      },
    },
  );

  const taxonomyAttributeAliases = SHOP_TAXONOMY_ALIASES;
  const servingConfig = getBrowseServingConfig();
  const fetchedEntry = fetchedTaxonomyPlp ? adaptTaxonomyPlpEntryToStore(fetchedTaxonomyPlp) : null;

  const entries: TaxonomyEntry[] = fetchedEntry
    ? mergeTaxonomyContentEntryIntoEntries([...neighborhoodMap], fetchedEntry)
    : [...neighborhoodMap];

  const { dispatch, getState } = getStore(
    {
      router: { pathname, query },
    },
    {
      content: {
        ...(settings ? adaptPageSettingsToStore(settings, pathname) : {}),
        ...(entries.length > 0 ? { entries } : {}),
      },
      navigation: {
        pageType: PageType.Taxonomy,
      },
      productsQuery: {
        canonicalFilters: { and: [] },
        facets: configFacets,
        page: 1,
        servingConfig,
        sortOptions,
        surface: SHOP_SURFACE_ALL,
        taxonomyAttributeAliases,
      },
      user: {
        bot: getMvtStrategyFromRequest(req) === 'bot',
        cognitoID: cognitoId || null,
        currencyCode,
        gaVisitorId,
      },
    },
  );

  batch(() => {
    dispatch(syncFromURL(req.url!));
    dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
    dispatch(userActions.setDeliveryZone(deliveryZoneCode));
    dispatch(userActions.setFeatureFlags(featureFlags));
  });

  // Build the listings request via the shared, store-free helper so this page and
  // the DISC-4347 preview endpoint run identical query-building. The store is still
  // seeded and synced above for the facet sidebar UI. The config-to-facets mapping
  // runs twice (here and for the store): pure and cheap, not worth a store restructure.
  const requestBody = buildListingsRequestFromUrl(req.url!, {
    contentEntries: entries,
    currency: currencyCode,
    deliveryZone: deliveryZoneCode,
    deliveryEvent: deliveryEvent && {
      date: deliveryEvent.last_deliver_by,
      name: deliveryEvent.name,
    },
    listingsControlsConfig: fetchedListingsControlsConfig,
  });

  const [internalLinks, , [siteChromeContent]] = await Promise.all([
    getInternalLinks(pathname),
    dispatch(requestProducts({ requestBody })),
    fetchGlobalContent({ featureFlags, urlPath: pathname }),
  ]);

  const state = getState();
  const { products } = state;

  if (products.error) {
    switch (products.error.name) {
      case PRODUCTS_LIST_DATA_FETCHING_ERROR.name:
        res.statusCode = StatusCode.NotFound;
        break;
      default:
        res.statusCode = StatusCode.InternalServerError;
        res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
        break;
    }
  }

  const pageEventData = getServerSidePageEventObject(
    publicRuntimeConfig.version,
    publicRuntimeConfig.appVersion,
    state,
  );

  const internalLinkGroups = internalLinks ?? { top: [], bottom: [] };

  const props: Partial<TaxonomyListingOwnProps> = {
    featureFlags,
    pageEventData,
    preview,
    siteChromeContent,
    stickyTopBanner: siteChromeContent ? siteChromeContent.topBannerMessages.length > 0 : false,
    ...selectPagePropsForClientHydration(state),
    topLinks: internalLinkGroups.top ?? [],
    bottomLinks: internalLinkGroups.bottom ?? [],
  };

  return {
    props,
  };
};

const ShopListingPage = (props: TaxonomyListingOwnProps) => {
  const { content } = props.updatedReduxState as ReduxApplicationState;

  const { handleToggle, isCuratorOpen, redirectToPDP } = useCurator();
  const pageTitle = useSelector(selectShopPageTitle);
  const { canonical, description, robots } = useTaxonomySeoTags();
  const breadcrumbLinks = useTaxonomyBreadcrumbs();

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <link href={canonical} rel="canonical" />
        {description && <meta content={description} name="description" />}
        {robots && <meta content={robots} name="robots" />}
        {/* A single-crumb trail is just "Homepage", so there is no hierarchy to describe. */}
        {breadcrumbLinks.length > 1 && (
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getLDBreadcrumbListSchema(breadcrumbLinks)),
            }}
            type="application/ld+json"
          />
        )}
      </Head>
      {content.departmentBanner && <DepartmentBanner {...content.departmentBanner} />}
      {content.error ? <NotFound /> : <TaxonomyListingContainer {...props} />}
      <Curator
        isOpen={isCuratorOpen}
        onProductClick={redirectToPDP}
        onToggle={handleToggle}
        showFavourties
      />
    </>
  );
};

export default ShopListingPage;
